import { DetailedGame, ScoringUpdates } from "@/data/match";
import { GameWithPlayers, sortScoringUpdates } from "@/helper/match";
import { isDuplicate } from "@/pages/admin/[seasonId]/bundesliga/gameschedule/debug/[id]";
import { Side } from "@prisma/client";

const firstServerByPoints = [0, 1, 4, 5, 8, 9, 12, 13, 16, 17];
const secondServerByPoints = [2, 3, 6, 7, 10, 11, 14, 15, 18, 19];

export function getServerFromScoringUpdates(
  game: DetailedGame & { scoringUpdates: ScoringUpdates[] }
) {
  sortScoringUpdates(game.scoringUpdates);
  const firstServerSide = getFirstServerSideForCurrentSet(game);
  const scoringUpdates = game.scoringUpdates.filter(
    (s) => s.type === "Point" || s.type === "EventSetStart"
  );

  const lastUpdate =
    scoringUpdates.length > 0
      ? scoringUpdates[scoringUpdates.length - 1]
      : null;
  const totalPoints =
    lastUpdate?.result
      ?.split(":")
      .reduce((acc, val) => acc + parseInt(val, 10), 0) ?? 0;

  // .at is not available in all browsers
  // const totalPoints =
  //   scoringUpdates
  //     .at(-1)
  //     ?.result?.split(":")
  //     .reduce((acc, val) => acc + parseInt(val), 0) ?? 0;

  if (totalPoints == null || !firstServerSide) return "error";
  const secondServerSide =
    firstServerSide === Side.Home ? Side.Away : Side.Home;

  if (firstServerByPoints.includes(totalPoints)) {
    return firstServerSide;
  } else if (secondServerByPoints.includes(totalPoints)) {
    return secondServerSide;
  } else {
    return totalPoints % 2 === 0 ? firstServerSide : secondServerSide;
  }
}
export function getFirstServerSideForCurrentSet(
  game: GameWithPlayers & { scoringUpdates: ScoringUpdates[] }
) {
  const filteredUpdates = game.scoringUpdates.filter(
    (s) => s.type === "EventSetStart"
  );
  const currentSet =
    filteredUpdates.length > 0
      ? filteredUpdates[filteredUpdates.length - 1]?.set
      : undefined;

  // .at is not available in all browsers
  // const currentSet = game.scoringUpdates
  //   .filter((s) => s.type === "EventSetStart")
  //   .at(-1)?.set;
  switch (currentSet) {
    case 1:
      return game.scoringUpdates.find(
        (s) => s.set === 1 && s.type === "EventSetStart"
      )?.playerId === game.homePlayer?.id
        ? Side.Home
        : Side.Away;
    case 2:
      return game.scoringUpdates.find(
        (s) => s.set === 2 && s.type === "EventSetStart"
      )?.playerId === game.homePlayer?.id
        ? Side.Home
        : Side.Away;
    case 3:
      return game.scoringUpdates.find(
        (s) => s.set === 3 && s.type === "EventSetStart"
      )?.playerId === game.homePlayer?.id
        ? Side.Home
        : Side.Away;
    case 4:
      return game.scoringUpdates.find(
        (s) => s.set === 4 && s.type === "EventSetStart"
      )?.playerId === game.homePlayer?.id
        ? Side.Home
        : Side.Away;
    case 5:
      return game.scoringUpdates.find(
        (s) => s.set === 5 && s.type === "EventSetStart"
      )?.playerId === game.homePlayer?.id
        ? Side.Home
        : Side.Away;
    default:
      return null;
  }
}

interface ServePointStats {
  homePointsOnServe: number;
  awayPointsOnServe: number;
  homePointsOnReturn: number;
  awayPointsOnReturn: number;
}

export function getPointsOnServeByGames(
  games: (DetailedGame & { scoringUpdates: ScoringUpdates[] })[]
) {
  const stats = games.reduce<ServePointStats>(
    (acc, game) => {
      const gameStats = getPointsOnServeByGame(game);
      acc.homePointsOnServe += gameStats.homePointsOnServe;
      acc.awayPointsOnServe += gameStats.awayPointsOnServe;
      acc.homePointsOnReturn += gameStats.homePointsOnReturn;
      acc.awayPointsOnReturn += gameStats.awayPointsOnReturn;
      return acc;
    },
    {
      homePointsOnServe: 0,
      awayPointsOnServe: 0,
      homePointsOnReturn: 0,
      awayPointsOnReturn: 0,
    }
  );

  return stats;
}

export function getPointsOnServeByGame(
  game: GameWithPlayers & { scoringUpdates: ScoringUpdates[] }
) {
  sortScoringUpdates(game.scoringUpdates);
  const scoringUpdates = game.scoringUpdates.filter(
    (s) => s.type === "Point" || s.type === "EventSetStart"
  );
  const firstServer =
    getFirstServerSideForCurrentSet(game) === Side.Home ? "home" : "away";
  const secondServer = firstServer === "home" ? "away" : "home";
  let lastUpdate: ScoringUpdates | null = null;
  const stats = scoringUpdates
    .filter((s) => s.type == "Point")
    .reduce<ServePointStats>(
      (acc, val) => {
        if (lastUpdate && isDuplicate(val, lastUpdate)) {
          return acc;
        }
        lastUpdate = val;
        if (val.result) {
          const totalPoints =
            val.result
              .split(":")
              .reduce((acc, val) => acc + parseInt(val), -1) ?? 0;
          const pointMadeBy =
            val.playerId === game.homePlayer?.id
              ? "home"
              : val.playerId === game.homeDouble?.id
                ? "home"
                : "away";
          const serveMadeBy = getServeMadeBy(
            totalPoints,
            firstServer,
            secondServer
          );
          if (pointMadeBy === "home") {
            if (serveMadeBy === "home") {
              acc.homePointsOnServe++;
            } else {
              acc.homePointsOnReturn++;
            }
          } else if (serveMadeBy === "away") {
            acc.awayPointsOnServe++;
          } else {
            acc.awayPointsOnReturn++;
          }
        }

        return acc;
      },
      {
        homePointsOnServe: 0,
        awayPointsOnServe: 0,
        homePointsOnReturn: 0,
        awayPointsOnReturn: 0,
      }
    );

  return stats;
}

function getServeMadeBy(
  totalPoints: number,
  firstServer: string,
  secondServer: string
): string {
  if (firstServerByPoints.includes(totalPoints)) {
    return firstServer;
  } else if (secondServerByPoints.includes(totalPoints)) {
    return secondServer;
  } else {
    return totalPoints % 2 === 0 ? firstServer : secondServer;
  }
}
